import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, ChevronDown, ChevronUp, CornerDownLeft } from 'lucide-react';
import './GlobalSearch.css';

// Global search (task #64) — an ALWAYS-VISIBLE inline search box, not a modal.
// Lives in the sidebar top (desktop) and the top header (mobile); results
// drop down anchored under the input as you type. Type-ahead with a 300ms
// debounce against /api/global-search; understands the prefix grammar
// (so:, sal:, inv:, po:, wo:, cust:, c:, ven:, v:, item:, it:).
//
// Results are grouped by record type with full keyboard navigation
// (ArrowUp/Down to move, Enter to open, Escape to close). SOs, POs, WOs,
// customers, vendors and items all deep-link to their record pages
// (/records/* + /native-items, tasks #62/#60); rows without a page yet
// (invoices, native-only twins) expand an inline info card.
//
// Ctrl/Cmd+K focuses the box (kept for muscle memory).

const PLACEHOLDER = 'Search… so:181582, item:RW2';

const fmtMoney = (n) =>
  n == null ? null : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TYPE_TAGS = {
  salesOrder: 'SO',
  invoice: 'INV',
  fulfillment: 'IF',
  payment: 'PMT',
  purchaseOrder: 'PO',
  workOrder: 'WO',
  customer: 'CUST',
  vendor: 'VEN',
  item: 'ITEM',
};

export default function GlobalSearch({ variant = 'sidebar' }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [parsedTerm, setParsedTerm] = useState('');
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [expandedId, setExpandedId] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  // Flatten grouped results for keyboard navigation.
  const flat = useMemo(() => groups.flatMap((g) => g.results), [groups]);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
    setExpandedId(null);
  }, []);

  // Ctrl/Cmd+K focuses the box (only the visible instance — the sidebar and
  // mobile-header copies are never displayed at the same time).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        const el = inputRef.current;
        if (el && el.offsetParent !== null) {
          e.preventDefault();
          el.focus();
          el.select();
          setOpen(true);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close on any click outside the box + dropdown.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  // Debounced type-ahead (300ms). Stale responses are dropped by sequence.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setGroups([]);
      setNote(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const seq = ++requestSeq.current;
      try {
        const { data } = await axios.get('/api/global-search', { params: { q } });
        if (seq !== requestSeq.current) return;
        setGroups(data.groups || []);
        setParsedTerm(data.parsed?.term || q);
        setNote(data.note || null);
        setError(null);
        setActiveIndex((data.groups || []).some((g) => g.results.length > 0) ? 0 : -1);
        setExpandedId(null);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setError(err.response?.data?.error || 'Search failed');
        setGroups([]);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const select = useCallback(
    (row) => {
      if (!row) return;
      if (row.url) {
        close();
        inputRef.current?.blur();
        navigate(row.url);
      } else {
        setExpandedId((prev) => (prev === row.id ? null : row.id));
      }
    },
    [close, navigate]
  );

  // "View all" — open the full-page results view (/search), optionally
  // pre-filtered to one group.
  const goToResults = useCallback(
    (type) => {
      const term = (parsedTerm || query).trim();
      if (term.length < 2) return;
      close();
      inputRef.current?.blur();
      navigate(`/search?q=${encodeURIComponent(term)}${type ? `&type=${encodeURIComponent(type)}` : ''}`);
    },
    [parsedTerm, query, close, navigate]
  );

  // Keep the active row scrolled into view.
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-row-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (expandedId) setExpandedId(null);
      else {
        close();
        inputRef.current?.blur();
      }
      return;
    }
    if (!open) setOpen(true);
    if (flat.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(flat[activeIndex] || flat[0]);
    }
  };

  let rowIndex = -1;
  const hasResults = flat.length > 0;
  const q = query.trim();

  return (
    <div className={`gsx-box gsx-${variant}`} ref={containerRef} role="search">
      <div className={`gsx-input-row ${open ? 'open' : ''}`}>
        <Search size={15} className="gsx-input-icon" />
        <input
          ref={inputRef}
          className="gsx-input"
          placeholder={PLACEHOLDER}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          aria-label="Global search"
          aria-expanded={open}
        />
        {loading && <span className="gsx-spinner" aria-hidden="true" />}
      </div>

      {open && (
        <div className="gsx-dropdown">
          <div className="gsx-results" ref={listRef}>
            {error && <div className="gsx-message gsx-error">{error}</div>}

            {!error && q.length < 2 && (
              <div className="gsx-message">
                <p>Search everything: orders, invoices, POs, work orders, customers, vendors, items.</p>
                <div className="gsx-hints">
                  <span><code>so:</code> / <code>sal:</code> sales orders</span>
                  <span><code>inv:</code> invoices</span>
                  <span><code>ful:</code> / <code>if:</code> fulfillments</span>
                  <span><code>pay:</code> / <code>pmt:</code> payments</span>
                  <span><code>po:</code> purchase orders</span>
                  <span><code>wo:</code> work orders</span>
                  <span><code>cust:</code> / <code>c:</code> customers</span>
                  <span><code>ven:</code> / <code>v:</code> vendors</span>
                  <span><code>item:</code> / <code>it:</code> items</span>
                </div>
              </div>
            )}

            {!error && q.length >= 2 && !loading && !hasResults && (
              <div className="gsx-message">{note || `No matches for "${q}".`}</div>
            )}

            {groups.map((group) => (
              group.results.length > 0 && (
                <div className="gsx-group" key={group.type}>
                  <div className="gsx-group-header">
                    <span>{group.label}</span>
                    {group.total > group.results.length ? (
                      <button
                        type="button"
                        className="gsx-group-viewall"
                        onClick={() => goToResults(group.type)}
                      >
                        View all {group.total.toLocaleString()} &rarr;
                      </button>
                    ) : (
                      <span className="gsx-group-count">{group.total.toLocaleString()}</span>
                    )}
                  </div>
                  {group.results.map((row) => {
                    rowIndex += 1;
                    const idx = rowIndex;
                    const isActive = idx === activeIndex;
                    const isExpanded = expandedId === row.id;
                    return (
                      <div key={row.id}>
                        <button
                          type="button"
                          data-row-index={idx}
                          className={`gsx-row ${isActive ? 'active' : ''}`}
                          onClick={() => select(row)}
                          onMouseEnter={() => setActiveIndex(idx)}
                        >
                          <span className="gsx-row-tag">{TYPE_TAGS[row.recordType] || ''}</span>
                          <span className="gsx-row-main">
                            {row.number && <code className="gsx-row-number">{row.number}</code>}
                            <span className="gsx-row-title">{row.title || '—'}</span>
                          </span>
                          <span className="gsx-row-meta">
                            {row.status && <span className="gsx-row-status">{row.status}</span>}
                            {row.amount != null && <span className="gsx-row-amount">{fmtMoney(row.amount)}</span>}
                            {row.date && <span className="gsx-row-date">{row.date}</span>}
                          </span>
                          <span className="gsx-row-action">
                            {row.url
                              ? <CornerDownLeft size={13} />
                              : isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                          </span>
                        </button>
                        {isExpanded && !row.url && (
                          <div className="gsx-card">
                            <dl className="gsx-card-grid">
                              {(row.fields || []).map((f) => (
                                <React.Fragment key={f.label}>
                                  <dt>{f.label}</dt>
                                  <dd>{typeof f.value === 'number' && /balance|remaining|overdue/i.test(f.label) ? fmtMoney(f.value) : String(f.value)}</dd>
                                </React.Fragment>
                              ))}
                            </dl>
                            <p className="gsx-card-note">No record page for this row type yet — key fields shown from the synced mirror.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            ))}
          </div>
          {hasResults && groups.some((g) => g.total > g.results.length) && (
            <div className="gsx-footer">
              <button type="button" className="gsx-footer-link" onClick={() => goToResults(null)}>
                View all results
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
